import { useEffect, useState } from 'react';
import { collection, getDocs, orderBy, query } from 'firebase/firestore';
import { ExternalLink, UserRound } from 'lucide-react';
import { db } from '../../firebase/config';
import { useSiteConfig } from '../../contexts/SiteConfigContext';
import SEO from '../SEO';

const StaffPhoto = ({ photoUrl, name }) => {
  const [failed, setFailed] = useState(false);
  return (
    <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-md border border-border/60 bg-muted/20">
      {photoUrl && !failed ? (
        <img
          src={photoUrl}
          alt={name}
          className="h-full w-full rounded-md object-cover"
          onError={() => setFailed(true)}
        />
      ) : (
        <UserRound className="h-5 w-5 text-muted-foreground" />
      )}
    </div>
  );
};

const Staff = () => {
  const { config } = useSiteConfig();
  const [people, setPeople] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchStaff = async () => {
      try {
        const snap = await getDocs(query(collection(db, 'staff'), orderBy('order', 'asc')));
        setPeople(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
      } catch {
        // Network/permission failure — render the empty state.
      } finally {
        setLoading(false);
      }
    };
    fetchStaff();
  }, []);

  function renderList() {
    if (loading) {
      return (
        <div className="grid gap-3 sm:grid-cols-2">
          {Array.from({ length: 4 }).map((_, i) => (
            <div key={i} className="h-28 animate-pulse rounded-md border bg-muted/40" />
          ))}
        </div>
      );
    }

    if (people.length === 0) {
      return (
        <div className="rounded-md border border-dashed px-6 py-16 text-center text-muted-foreground">
          Nothing here yet.
        </div>
      );
    }

    return (
      <ul className="grid gap-3 sm:grid-cols-2">
        {people.map((p) => (
          <li
            key={p.id}
            className="flex items-start gap-4 rounded-md border border-border/60 bg-background/40 p-5"
          >
            <StaffPhoto photoUrl={p.photoUrl} name={p.name} />
            <div className="min-w-0">
              <div className="flex items-center gap-2">
                <h2 className="truncate font-display text-lg font-semibold">{p.name}</h2>
                {p.link && (
                  <a
                    href={p.link}
                    target="_blank"
                    rel="noreferrer"
                    aria-label={`Link for ${p.name}`}
                    className="text-muted-foreground transition-colors hover:text-foreground"
                  >
                    <ExternalLink className="h-3.5 w-3.5" />
                  </a>
                )}
              </div>
              <div className="mt-0.5 font-mono text-[10px] uppercase tracking-[0.25em] text-muted-foreground">
                {p.role}
              </div>
              {p.description && (
                <p className="mt-2 text-sm text-muted-foreground">{p.description}</p>
              )}
            </div>
          </li>
        ))}
      </ul>
    );
  }

  return (
    <div className="mx-auto max-w-4xl px-4 py-10 sm:px-6">
      <SEO
        title="Staff & Contributors"
        description="The people who build, run, and support KZCOMP — developers, tournament admins, mappers, and community contributors."
        canonicalPath="/staff"
      />

      <header className="mb-8 space-y-2">
        <h1 className="text-3xl font-bold tracking-tight">Staff &amp; Contributors</h1>
        {config.staff?.description && (
          <p className="text-muted-foreground">{config.staff.description}</p>
        )}
      </header>

      {renderList()}
    </div>
  );
};

export default Staff;
